import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { Dropzone } from "components/Documents/shared/DocumentUploadModal";
import {
  DatePicker,
  MaskedInput,
  Select,
  STRING_TO_DATE,
  TextInput,
} from "components/inputs";
import Toggle from "components/inputs/Toggle/Toggle";
import {
  getFilteredServiceOptions,
  ServiceMultiSelect,
} from "components/ServiceMultiSelect/ServiceMultiSelect";
import { format } from "date-fns";
import debounce from "lodash/debounce";
import DateOfInjuryForm, {
  type ClaimNumberType,
  type DateOfInjuryType,
} from "pages/files/DateOfInjuryForm";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetClaimantsQuery } from "services/claimant-api";
import type { Section111ClaimantMatch } from "services/claims-api";
import { useGetMatchingClaimsForClaimantQuery } from "services/claims-api";
import { useGetClientsQuery } from "services/client-api";
import { useCreateFileMutation, useGetFilesQuery } from "services/file-api";
import { useGetRresQuery } from "services/rre-api";
import type { CreateFileData, FileType } from "types/DataTypes";
import { DATE_TO_STRING } from "utils/date-utils";
import { hasApiError } from "utils/error-utils";
import {
  formatClaimantLabel,
  formatOptionLabel,
  selectStyles,
} from "utils/format-utils";
import { useSelector } from "utils/redux";

import ExistingClaimantFileModal, {
  type ClaimantMatchParams,
} from "./ExistingClaimantFileModal";
import ExistingClaimFileModal from "./ExistingClaimFileModal";
import KaboodleModal from "./KaboodleModal";
import Section111ClaimantMatchesModal from "./Section111ClaimantMatchesModal";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type OptionType = {
  label: string;
  value: string;
  sortKey?: string;
};

type FormData = {
  claimant_ids: OptionType;
  client_id: OptionType;
  dates_of_injury: { date: string; claim_numbers: { number: string }[] }[];
  services: OptionType[];
  rre: OptionType;
  opening_type: OptionType;
  first_name: string;
  last_name: string;
  birth_date: string;
  ssn: string;
  icn: string;
  file: File;
  due_date: string;
};

const openingTypeOptions = [
  { label: "Individual Claimant", value: "Individual" },
  { label: "RRE (Import)", value: "Mass" },
];

const initialFormState = {
  claimant_ids: { label: "", value: "" },
  client_id: { label: "", value: "" },
  dates_of_injury: [{ date: "" }],
  services: [],
  opening_type: { label: "", value: "" },
  first_name: "",
  last_name: "",
  birth_date: "",
  ssn: "",
  icn: "",
  due_date: "",
};

// Key used to remember duplicate-claimant prompts the user has dismissed.
const claimantMatchKey = (params: ClaimantMatchParams): string =>
  [
    params.ssn,
    params.first_name.toLowerCase(),
    params.last_name.toLowerCase(),
    params.birth_date,
  ].join("|");

const CreateFileModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.main.loggedInUser);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FormData>();

  const [createFile, { error, isSuccess }] = useCreateFileMutation();

  useEffect(() => {
    if (hasApiError(error)) {
      Object.entries(error.data).forEach(([field, messages]) => {
        setError(field as keyof FormData, {
          type: "manual",
          message: Array.isArray(messages) ? messages[0] : messages,
        });
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (isOpen) {
      reset(initialFormState);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  const [clientOptions, setClientOptions] = useState<OptionType[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState<string>("");

  const debouncedSetClientSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setClientSearchTerm(value);
      }, 500),
    [],
  );

  // only run the clients query when there's a search term
  const { data: clients, isFetching: isClientsFetching } = useGetClientsQuery(
    { searchTerm: clientSearchTerm },
    { skip: !isOpen || !clientSearchTerm },
  );

  useEffect(() => {
    if (!clientSearchTerm) {
      // clear options when search term is empty
      setClientOptions([]);
    } else if (clients) {
      const options =
        clients.results?.map((client) => ({
          label: client.name,
          value: client.id.toString(),
        })) ?? [];
      setClientOptions(options);
    }
  }, [clients, clientSearchTerm]);

  const [claimantsOptions, setClaimantsOptions] = useState<OptionType[]>([]);

  // only run the query when the modal is open to avoid unnecessary network requests
  const { data: claimants } = useGetClaimantsQuery({}, { skip: !isOpen });

  useEffect(() => {
    if (claimants) {
      setClaimantsOptions([
        {
          label: "Create a Claimant",
          value: "",
          sortKey: "",
        },
        ...(claimants.results
          ?.map((claimant) => ({
            label: formatClaimantLabel(claimant),
            value: claimant.id.toString(),
            sortKey: claimant.last_name.toLowerCase(),
          }))
          .sort((a, b) => a.sortKey.localeCompare(b.sortKey)) ?? []),
      ]);
    }
  }, [claimants]);

  const [rresOptions, setRresOptions] = useState<OptionType[]>([]);

  // only run the RREs query when the modal is open
  const { data: rres } = useGetRresQuery({}, { skip: !isOpen });

  useEffect(() => {
    if (rres) {
      setRresOptions(
        rres?.results.map((rre) => ({
          label: rre.name,
          value: rre.id.toString(),
        })),
      );
    }
  }, [rres]);

  const [selectedServices, setSelectedServices] = useState<OptionType[]>([]);
  const [openingType, setOpeningType] = useState<OptionType | null>(null);

  const serviceOptions = loggedInUser?.active_licensed_services || [];

  const filteredServiceOptions = useMemo(() => {
    if (!selectedServices.length) return serviceOptions;

    return getFilteredServiceOptions(selectedServices, serviceOptions);
  }, [selectedServices, serviceOptions]);

  const hasSection111Service = selectedServices.some(
    (service) => service.value === "111",
  );

  const hasMiscellaneousService = selectedServices.some(
    (service) =>
      service.value === "CON" ||
      service.value === "AUD" ||
      service.value === "S111G" ||
      service.value === "SWL",
  );

  const showExtraFields =
    (!hasSection111Service ||
      (hasSection111Service &&
        openingType &&
        openingType.value === "Individual")) &&
    !hasMiscellaneousService &&
    selectedServices.length > 0;

  const showClaimantFields =
    (!hasSection111Service ||
      (hasSection111Service &&
        openingType &&
        openingType.value === "Individual")) &&
    selectedServices.length > 0;

  const showDocumentUpload =
    hasSection111Service && openingType && openingType.value === "Mass";

  const showICN =
    hasSection111Service && openingType && openingType.value === "Individual";

  const [isCreatingClaimant, setIsCreatingClaimant] = useState(false);

  useEffect(() => {
    if (isCreatingClaimant) {
      setValue("claimant_ids", { label: "", value: "" });
    } else {
      setValue("first_name", "");
      setValue("last_name", "");
      setValue("birth_date", "");
      setValue("ssn", "");
    }
  }, [isCreatingClaimant, setValue]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = useCallback(() => {
    reset(initialFormState);
    setOpeningType(null);
    setSelectedServices([]);
    setIsCreatingClaimant(false);
    setSelectedFile(null);
    setClientSearchTerm("");
    onClose();
  }, [reset, onClose]);

  // --- Existing-claim detection ------------------------------------------
  // As the user types claim numbers on a new file, look them up against
  // existing files. On an exact match we prompt to use / import that file.
  const [claimNumberToCheck, setClaimNumberToCheck] = useState("");
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [matchedFile, setMatchedFile] = useState<FileType | null>(null);
  const [matchCount, setMatchCount] = useState(0);
  const [matchedClaimNumber, setMatchedClaimNumber] = useState("");
  // Claim numbers the user has already resolved (imported / opened / kept as
  // new) so we don't re-prompt for them. A ref keeps the memoized onChange
  // handler from reading a stale set.
  const dismissedClaimNumbersRef = useRef<Set<string>>(new Set());

  // Force a remount of DateOfInjuryForm (which only reads its initial props
  // once) when importing an existing file's dates of injury.
  const [doiFormKey, setDoiFormKey] = useState(0);
  const [importedDatesOfInjury, setImportedDatesOfInjury] = useState<
    DateOfInjuryType[]
  >([]);
  const [importedClaimNumbers, setImportedClaimNumbers] = useState<
    ClaimNumberType[]
  >([]);

  const debouncedSetClaimNumberToCheck = useMemo(
    () =>
      debounce((value: string) => {
        setClaimNumberToCheck(value);
      }, 500),
    [],
  );

  const { data: matchingFiles } = useGetFilesQuery(
    { filters: { claim_number: claimNumberToCheck } },
    { skip: !isOpen || !claimNumberToCheck },
  );

  useEffect(() => {
    if (!claimNumberToCheck) return;
    if (
      dismissedClaimNumbersRef.current.has(claimNumberToCheck.toLowerCase())
    ) {
      return;
    }
    const results = matchingFiles?.results;
    if (results && results.length > 0) {
      setMatchedFile(results[0]);
      setMatchCount(matchingFiles?.count ?? results.length);
      setMatchedClaimNumber(claimNumberToCheck);
      setShowExistingModal(true);
    }
  }, [matchingFiles, claimNumberToCheck]);

  // Reset detection state whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      dismissedClaimNumbersRef.current = new Set();
      setClaimNumberToCheck("");
      setShowExistingModal(false);
      setMatchedFile(null);
    }
  }, [isOpen]);

  // --- Duplicate-claimant detection --------------------------------------
  // As the user fills in the Create Claimant fields, look the combination up
  // against existing files' claimants. A complete SSN, or the full name plus
  // date of birth, is enough to check; on a match we prompt to use the
  // existing file instead.
  const [claimantToCheck, setClaimantToCheck] =
    useState<ClaimantMatchParams | null>(null);
  const [showExistingClaimantModal, setShowExistingClaimantModal] =
    useState(false);
  const [claimantMatchedFile, setClaimantMatchedFile] =
    useState<FileType | null>(null);
  const [claimantMatchCount, setClaimantMatchCount] = useState(0);
  const [matchedClaimantParams, setMatchedClaimantParams] =
    useState<ClaimantMatchParams | null>(null);
  // Combinations the user has already resolved (opened / kept as new) so we
  // don't re-prompt for them. A ref keeps memoized callbacks from reading a
  // stale set.
  const dismissedClaimantKeysRef = useRef<Set<string>>(new Set());

  const debouncedSetClaimantToCheck = useMemo(
    () =>
      debounce((params: ClaimantMatchParams | null) => {
        setClaimantToCheck(params);
      }, 500),
    [],
  );

  const watchedFirstName = watch("first_name");
  const watchedLastName = watch("last_name");
  const watchedBirthDate = watch("birth_date");
  const watchedSsn = watch("ssn");

  useEffect(() => {
    if (!isCreatingClaimant) {
      debouncedSetClaimantToCheck(null);
      return;
    }
    const params: ClaimantMatchParams = {
      ssn: (watchedSsn ?? "").replace(/\D/g, ""),
      first_name: (watchedFirstName ?? "").trim(),
      last_name: (watchedLastName ?? "").trim(),
      birth_date: watchedBirthDate ?? "",
    };
    const isCheckable =
      params.ssn.length === 9 ||
      Boolean(params.first_name && params.last_name && params.birth_date);
    debouncedSetClaimantToCheck(isCheckable ? params : null);
  }, [
    isCreatingClaimant,
    watchedSsn,
    watchedFirstName,
    watchedLastName,
    watchedBirthDate,
    debouncedSetClaimantToCheck,
  ]);

  const {
    currentData: claimantMatchingFiles,
    isFetching: isFetchingClaimantMatchingFiles,
    isUninitialized: isUninitializedClaimantMatchingFiles,
  } = useGetFilesQuery(
    {
      filters: {
        claimant_match: true,
        claimant_ssn: claimantToCheck?.ssn ?? "",
        claimant_first_name: claimantToCheck?.first_name ?? "",
        claimant_last_name: claimantToCheck?.last_name ?? "",
        claimant_birth_date: claimantToCheck?.birth_date ?? "",
      },
    },
    { skip: !isOpen || !claimantToCheck },
  );

  const hasUndismissedClaimantFileMatch = Boolean(
    claimantToCheck &&
    (claimantMatchingFiles?.results?.length ?? 0) > 0 &&
    !dismissedClaimantKeysRef.current.has(claimantMatchKey(claimantToCheck)),
  );

  useEffect(() => {
    if (!claimantToCheck) return;
    if (dismissedClaimantKeysRef.current.has(claimantMatchKey(claimantToCheck)))
      return;
    const results = claimantMatchingFiles?.results;
    if (results && results.length > 0) {
      setClaimantMatchedFile(results[0]);
      setClaimantMatchCount(claimantMatchingFiles?.count ?? results.length);
      setMatchedClaimantParams(claimantToCheck);
      setShowExistingClaimantModal(true);
    }
  }, [claimantMatchingFiles, claimantToCheck]);

  // Reset duplicate-claimant detection whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      dismissedClaimantKeysRef.current = new Set();
      setClaimantToCheck(null);
      setShowExistingClaimantModal(false);
      setClaimantMatchedFile(null);
      setMatchedClaimantParams(null);
    }
  }, [isOpen]);

  const dismissClaimantMatch = useCallback(() => {
    if (matchedClaimantParams) {
      dismissedClaimantKeysRef.current.add(
        claimantMatchKey(matchedClaimantParams),
      );
    }
    setShowExistingClaimantModal(false);
  }, [matchedClaimantParams]);

  const handleUseExistingClaimantFile = useCallback(() => {
    if (!claimantMatchedFile) return;
    handleClose();
    navigate(`/operations/files/${claimantMatchedFile.id}/edit`);
  }, [claimantMatchedFile, navigate, handleClose]);

  const [showSection111Modal, setShowSection111Modal] = useState(false);
  const dismissedSection111KeysRef = useRef<Set<string>>(new Set());

  const {
    currentData: section111Matches = [],
    isFetching: isFetchingSection111Matches,
    isUninitialized: isUninitializedSection111Matches,
    isError: isErrorSection111Matches,
  } = useGetMatchingClaimsForClaimantQuery(
    {
      ssn: claimantToCheck?.ssn,
      firstName: claimantToCheck?.first_name,
      lastName: claimantToCheck?.last_name,
      birthDate: claimantToCheck?.birth_date,
    },
    {
      skip: !isOpen || !claimantToCheck || hasUndismissedClaimantFileMatch,
    },
  );

  useEffect(() => {
    setShowSection111Modal(false);
  }, [claimantToCheck]);

  useEffect(() => {
    if (!claimantToCheck) return;
    if (hasUndismissedClaimantFileMatch) {
      setShowSection111Modal(false);
      return;
    }
    if (
      isFetchingSection111Matches ||
      isFetchingClaimantMatchingFiles ||
      isUninitializedSection111Matches ||
      isUninitializedClaimantMatchingFiles
    ) {
      return;
    }
    if (
      dismissedSection111KeysRef.current.has(claimantMatchKey(claimantToCheck))
    ) {
      return;
    }
    setShowSection111Modal(section111Matches.length > 0);
  }, [
    section111Matches,
    isFetchingSection111Matches,
    isFetchingClaimantMatchingFiles,
    isUninitializedSection111Matches,
    isUninitializedClaimantMatchingFiles,
    claimantToCheck,
    hasUndismissedClaimantFileMatch,
  ]);

  useEffect(() => {
    if (isOpen) {
      dismissedSection111KeysRef.current = new Set();
      setShowSection111Modal(false);
    }
  }, [isOpen]);

  const dismissSection111Match = useCallback(() => {
    if (claimantToCheck) {
      dismissedSection111KeysRef.current.add(claimantMatchKey(claimantToCheck));
    }
    setShowSection111Modal(false);
  }, [claimantToCheck]);

  const handleSelectSection111Match = useCallback(
    (match: Section111ClaimantMatch) => {
      const isoBirthDate = match.injured_party_dob
        ? DATE_TO_STRING(STRING_TO_DATE(match.injured_party_dob), "yyyy-MM-dd")
        : null;

      if (match.injured_party_first_name) {
        setValue("first_name", match.injured_party_first_name);
      }
      if (match.injured_party_last_name) {
        setValue("last_name", match.injured_party_last_name);
      }
      if (isoBirthDate) {
        setValue("birth_date", isoBirthDate);
      }
      if (match.injured_party_ssn) {
        setValue("ssn", match.injured_party_ssn);
      }
      setShowSection111Modal(false);
      if (claimantToCheck) {
        const filledParams: ClaimantMatchParams = {
          ssn: match.injured_party_ssn
            ? match.injured_party_ssn.replace(/\D/g, "")
            : claimantToCheck.ssn,
          first_name:
            match.injured_party_first_name?.trim() ||
            claimantToCheck.first_name,
          last_name:
            match.injured_party_last_name?.trim() || claimantToCheck.last_name,
          birth_date: isoBirthDate || claimantToCheck.birth_date,
        };
        dismissedSection111KeysRef.current.add(
          claimantMatchKey(claimantToCheck),
        );
        dismissedSection111KeysRef.current.add(claimantMatchKey(filledParams));
      }
    },
    [setValue, claimantToCheck],
  );

  const handleDatesOfInjuryChange = useCallback(
    (
      data: Array<{
        date: string;
        claim_numbers: Array<{ number: string; id?: number }>;
      }>,
    ) => {
      setValue("dates_of_injury", data);

      const enteredNumbers = data
        .flatMap((doi) => doi.claim_numbers.map((cn) => cn.number.trim()))
        .filter(Boolean);
      // Check the most recently entered number that hasn't been resolved yet.
      const candidate =
        [...enteredNumbers]
          .reverse()
          .find(
            (num) => !dismissedClaimNumbersRef.current.has(num.toLowerCase()),
          ) ?? "";
      debouncedSetClaimNumberToCheck(candidate);
    },
    [setValue, debouncedSetClaimNumberToCheck],
  );

  const dismissExistingMatch = useCallback((claimNumber: string) => {
    dismissedClaimNumbersRef.current.add(claimNumber.toLowerCase());
    setShowExistingModal(false);
    setClaimNumberToCheck("");
  }, []);

  const handleUseExisting = useCallback(() => {
    if (!matchedFile) return;
    handleClose();
    navigate(`/operations/files/${matchedFile.id}/edit`);
  }, [matchedFile, navigate, handleClose]);

  const handleImportExisting = useCallback(() => {
    if (!matchedFile) return;

    // Client
    if (matchedFile.client) {
      setValue("client_id", {
        label: matchedFile.client.name,
        value: String(matchedFile.client.id),
      });
    }

    // Services (match to licensed-service options so labels line up)
    const importedServices: OptionType[] = (matchedFile.services ?? []).map(
      (service) => {
        const option = serviceOptions.find(
          (opt) => String(opt.value) === service.type,
        );
        return {
          label:
            option?.label ||
            service.type_verbose ||
            service.label ||
            service.type,
          value: service.type,
        };
      },
    );
    setSelectedServices(importedServices);
    setValue("services", importedServices);

    const has111 = importedServices.some((service) => service.value === "111");
    if (has111) {
      const individualOption = openingTypeOptions[0];
      setOpeningType(individualOption);
      setValue("opening_type", individualOption);
      if (matchedFile.rre != null) {
        // rresOptions is loaded on modal open; if the user imports before it
        // resolves the RRE is skipped (rare — the modal must be open long
        // enough to have surfaced a match first).
        const rreOption = rresOptions.find(
          (opt) => opt.value === String(matchedFile.rre),
        );
        if (rreOption) setValue("rre", rreOption);
      }
    }

    // Claimant (prefer selecting the existing claimant)
    const claimant = matchedFile.claimants?.[0];
    if (claimant) {
      setIsCreatingClaimant(false);
      setValue("claimant_ids", {
        label: formatClaimantLabel(claimant),
        value: String(claimant.id),
      });
    }

    // Dates of injury + claim numbers (remount the sub-form with these values)
    setImportedDatesOfInjury(
      (matchedFile.dates_of_injury ?? []).map((doi) => ({
        id: doi.id,
        date: DATE_TO_STRING(STRING_TO_DATE(doi.date)) ?? "",
        file: 0,
      })),
    );
    setImportedClaimNumbers(
      (matchedFile.claim_numbers ?? []).map((cn) => ({
        id: cn.id,
        number: cn.number,
        date_of_injury_id: cn.date_of_injury_id,
        file: 0,
      })),
    );
    setDoiFormKey((key) => key + 1);

    // Don't re-prompt for any claim number we just imported.
    (matchedFile.claim_numbers ?? []).forEach((cn) =>
      dismissedClaimNumbersRef.current.add(cn.number.toLowerCase()),
    );
    dismissExistingMatch(matchedClaimNumber);
  }, [
    matchedFile,
    matchedClaimNumber,
    serviceOptions,
    rresOptions,
    setValue,
    dismissExistingMatch,
  ]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data: FormData) => {
      const transformedData: CreateFileData = {
        claimant_ids:
          data.claimant_ids.value === "" ? [] : [data.claimant_ids.value],
        client_id: data.client_id.value,
        // confirm that dates_of_injury exists and is an array and that the first element is not empty
        dates_of_injury:
          !data.dates_of_injury ||
          !data.dates_of_injury[0] ||
          data.dates_of_injury[0].date === ""
            ? []
            : data.dates_of_injury,
        services: data.services.map((service) => ({
          type: service.value,
        })),
        rre: data.rre?.value,
        claimant:
          data.first_name !== "" && data.last_name !== "" && data.birth_date
            ? {
                first_name: data.first_name,
                last_name: data.last_name,
                birth_date: data.birth_date,
                ...(data.ssn ? { ssn: data.ssn } : {}),
              }
            : undefined,
        icn: data.icn,
        due_date: data.due_date,
      };
      const formData = new FormData();

      formData.append("file_data", JSON.stringify(transformedData));

      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      try {
        const result = await createFile({
          data: formData,
        }).unwrap();

        if (result.id) {
          reset();
          onClose();
          navigate(`/operations/files/${result.id}/edit`);
        }
      } catch (err) {
        console.error("Failed to create File:", err);
      }
    },
    [createFile, reset, onClose, navigate, selectedFile],
  );

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <>
      <KaboodleModal
        isOpen={isOpen}
        onClose={handleClose}
        footer={footerContent}
      >
        <Card heading="Create File">
          <Controller
            name="client_id"
            control={control}
            rules={{ required: "Client is required" }}
            render={({ field: { name, onChange, value } }) => (
              <Select
                name={name}
                label="Client Name"
                options={clientOptions}
                value={value?.value ? value : null}
                errors={errors}
                placeholder="Search..."
                noOptionsMessage={({ inputValue }) =>
                  !inputValue
                    ? "Begin typing to search for clients..."
                    : isClientsFetching
                      ? "Searching..."
                      : "No clients found."
                }
                onInputChange={(inputValue: string) =>
                  debouncedSetClientSearchTerm(inputValue)
                }
                onChange={onChange}
                required={true}
              />
            )}
          />
          <Controller
            control={control}
            name="services"
            rules={{ required: "Services are required" }}
            render={({ field: { onChange: onControllerChange, value } }) => (
              <ServiceMultiSelect
                label="Services Selected"
                name="services"
                errors={errors}
                options={filteredServiceOptions}
                value={value}
                onChange={(selectedOptions) => {
                  setSelectedServices(selectedOptions as OptionType[]);
                  if (
                    Array.isArray(selectedOptions) &&
                    !selectedOptions.some((option) => option.value === "111")
                  ) {
                    setOpeningType(null);
                  }
                  onControllerChange(selectedOptions);
                }}
                required={true}
              />
            )}
          />
          <Controller
            name="due_date"
            control={control}
            rules={{ required: "Due Date is required" }}
            render={({ field: { name, onChange, value } }) => (
              <DatePicker
                name={name}
                label="Due Date"
                initialDate={STRING_TO_DATE(value)}
                allowFutureDates={true}
                onDateChange={(date) =>
                  onChange(date ? format(date, "MM/dd/yyyy") : null)
                }
                errors={errors}
                required={true}
              />
            )}
          />
          {hasSection111Service && (
            <Controller
              name="opening_type"
              control={control}
              render={({ field: { name, onChange, value } }) => {
                return (
                  <Select
                    name={name}
                    label="Opening Type "
                    options={openingTypeOptions}
                    value={value?.value ? value : null}
                    errors={errors}
                    onChange={(option) => {
                      setOpeningType(option as OptionType);
                      onChange(option);
                    }}
                    required={true}
                  />
                );
              }}
            />
          )}
          {hasSection111Service && (
            <Controller
              name="rre"
              control={control}
              render={({ field: { name, onChange, value } }) => {
                return (
                  <Select
                    name={name}
                    label="RRE"
                    options={rresOptions}
                    value={value?.value ? value : null}
                    errors={errors}
                    onChange={onChange}
                    required={true}
                  />
                );
              }}
            />
          )}

          {showExtraFields && (
            <DateOfInjuryForm
              key={doiFormKey}
              initialClaimNumbers={importedClaimNumbers}
              initialDatesOfInjury={importedDatesOfInjury}
              onChange={handleDatesOfInjuryChange}
            />
          )}

          {showClaimantFields && (
            <>
              <div className="row">
                <label>Claimant Type</label>
                <figure>
                  <Toggle
                    checked={isCreatingClaimant}
                    onChange={setIsCreatingClaimant}
                    leftLabel="Select Claimant"
                    rightLabel="Create Claimant"
                  />
                </figure>
              </div>

              {!isCreatingClaimant ? (
                <Controller
                  name="claimant_ids"
                  control={control}
                  rules={{ required: !hasMiscellaneousService }}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="claimant_ids"
                      label="Select Existing Claimant"
                      options={claimantsOptions.filter(
                        (opt) => opt.value !== "",
                      )}
                      value={value?.value ? value : null}
                      errors={errors}
                      onChange={onChange}
                      styles={selectStyles}
                      formatOptionLabel={(option, context) =>
                        formatOptionLabel(option as OptionType, context, true)
                      }
                    />
                  )}
                />
              ) : (
                <>
                  <Controller
                    name="birth_date"
                    control={control}
                    rules={{ required: !hasMiscellaneousService }}
                    render={({ field: { name, onChange, value } }) => (
                      <DatePicker
                        key={value}
                        name={name}
                        label="Date of Birth"
                        initialDate={STRING_TO_DATE(value)}
                        onDateChange={(date) =>
                          onChange(date ? format(date, "yyyy-MM-dd") : null)
                        }
                        errors={errors}
                      />
                    )}
                  />
                  <Controller
                    name="first_name"
                    control={control}
                    rules={{ required: !hasMiscellaneousService }}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        label="First Name"
                        errors={errors}
                      />
                    )}
                  />
                  <Controller
                    name="last_name"
                    control={control}
                    rules={{ required: !hasMiscellaneousService }}
                    render={({ field }) => (
                      <TextInput {...field} label="Last Name" errors={errors} />
                    )}
                  />
                  <Controller
                    name="ssn"
                    control={control}
                    render={({ field }) => (
                      <MaskedInput
                        {...field}
                        mask="ssn"
                        label="SSN"
                        errors={errors}
                      />
                    )}
                  />
                </>
              )}
            </>
          )}
          {showICN && (
            <Controller
              name="icn"
              control={control}
              rules={{ required: "ICN is required" }}
              render={({ field }) => {
                return <TextInput {...field} label="ICN" errors={errors} />;
              }}
            />
          )}
          {showDocumentUpload && (
            <div className="data-values">
              <label>Claim Import:</label>
              <Dropzone
                selectedFile={selectedFile}
                // @ts-expect-error - TODO: fix this
                onFileSelect={setSelectedFile}
                fileInputRef={fileInputRef}
              />
            </div>
          )}
        </Card>
      </KaboodleModal>
      {matchedFile && (
        <ExistingClaimFileModal
          isOpen={showExistingModal}
          claimNumber={matchedClaimNumber}
          matchedFile={matchedFile}
          matchCount={matchCount}
          onUseExisting={handleUseExisting}
          onImport={handleImportExisting}
          onDismiss={() => dismissExistingMatch(matchedClaimNumber)}
        />
      )}
      {claimantMatchedFile && matchedClaimantParams && (
        <ExistingClaimantFileModal
          isOpen={showExistingClaimantModal}
          matchParams={matchedClaimantParams}
          matchedFile={claimantMatchedFile}
          matchCount={claimantMatchCount}
          onUseExisting={handleUseExistingClaimantFile}
          onDismiss={dismissClaimantMatch}
        />
      )}
      <Section111ClaimantMatchesModal
        isOpen={showSection111Modal}
        matches={section111Matches}
        isFetching={isFetchingSection111Matches}
        isError={isErrorSection111Matches}
        onSelect={handleSelectSection111Match}
        onDismiss={dismissSection111Match}
      />
    </>
  );
};

export default CreateFileModal;
